import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

import { Task } from '@lit/task';

import { FlakinessReport, Schema } from '@flakiness/flakiness-report';
import { ReportUtils } from '@flakiness/sdk/browser';
import { SyncComputationCache } from '@flakiness/server/common/computationCache.js';
import { Query } from '@flakiness/server/common/fql/query.js';
import { Ranges } from '@flakiness/server/common/ranges.js';
import { EnvGroup } from '@flakiness/server/common/stats/envGroup.js';
import { Histogram } from '@flakiness/server/common/stats/histogram.js';
import { ResolvedTimeline } from '@flakiness/server/common/stats/resolvedTimeline.js';
import { SpanAnalyzer } from '@flakiness/server/common/stats/spanAnalyzer.js';
import { Stats } from '@flakiness/server/common/stats/stats.js';
import { StatsAnalyzer } from '@flakiness/server/common/stats/statsAnalyzer.js';
import { StatsBuilder } from '@flakiness/server/common/stats/statsBuilder.js';
import { FilterContext, TestIndex } from '@flakiness/server/common/stats/testIndex.js';
import { TestIndexBuilder } from '@flakiness/server/common/stats/testIndexBuilder.js';
import { TestOutcomes as T } from '@flakiness/server/common/stats/testOutcomes.js';
import { TestsReport } from '@flakiness/server/common/stats/testsReport.js';
import { TestHistory, TimelineTestsReport } from '@flakiness/server/common/stats/timelineTestsReport.js';
import { WireReport } from '@flakiness/server/common/wireReport.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { classMap } from 'lit/directives/class-map.js';
import { Temporal } from 'temporal-polyfill';
import { ZodError } from 'zod/v4';
import { api } from '../api.js';
import { AttachmentUrlBuilder } from '../artifact.js';
import { linkStyles, pageStyles } from '../components/cssstyles.js';
import { DataTableLinker, DataTableProvider } from '../components/data-table.js';
import { FqlChangedEvent } from '../components/fql-search.js';
import { FKReportHistorySelectEvent } from '../components/report-history-selector.js';
import { TestRunState } from '../components/test-run.js';
import { ContextTask, consume, contexts } from '../contexts.js';
import { createAnnotationStatsDataProvider, createAnnotationStatsLoaderLocal } from '../data-table-providers/annotationsProvider.js';
import { createErrorStatsDataProvider, createErrorStatsLoaderLocal } from '../data-table-providers/errorsProvider.js';
import { TestHistorySnippetSelection, createDurationTrendColumn, createTestHistoryColumn, createTestHistorySnippet } from '../data-table-providers/genericColumns.js';
import { createTagStatsDataProvider, createTagStatsLoaderLocal } from '../data-table-providers/tagsProvider.js';
import { createStatusColumn, createTestStatsLoaderLocal, testStatsColumns } from '../data-table-providers/testStatsProvider.js';
import { createTimelineStatsDataProvider, createTimelineStatsLoaderLocal } from '../data-table-providers/timelineStatsProvider.js';
import { renderCategoryIconColor } from '../fragments.js';
import { AppLink, Linkifier, handleLink, hrefOrClick } from '../linkifier.js';
import { tasks } from '../tasks.js';
import { FKHistoryRequested, FeatureCommitHistoryPopup } from './feature-commits-history.js';
import { PlaneName } from './feature-tests-report.js';

export type FeatureReportViewerState = {
  timelineSplit?: EnvGroup,
  query?: string,
  testId?: Stats.TestId,
  testTimeline?: EnvGroup,
  testAttempt?: number,
  plane?: PlaneName,
  page?: number,
  pageSize?: number,
  sortName?: string,
  sortDirection?: WireTypes.SortDirection,
  // Local override of the project's default history type. Absent => follow the project default.
  historyType?: WireTypes.HistoryType,
};

@customElement('feature-report-viewer')
export class FeatureReportViewer extends LitElement {
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;
  @consume({ context: contexts.commitRegistry, subscribe: true }) private _commitRegistry?: ContextType<typeof contexts.commitRegistry>;
  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;

  @property({ attribute: false }) historyHead?: string;
  @property({ attribute: false }) reportId?: Stats.RunId;
  @property({ attribute: false }) report?: FlakinessReport.Report;
  @property({ attribute: false }) commit?: WireTypes.Commit;

  @property({ attribute: false }) attachmentURLBuilder?: AttachmentUrlBuilder;

  @property({ attribute: false }) state?: FeatureReportViewerState;
  @property({ attribute: false }) linkifier?: Linkifier<FeatureReportViewerState>;

  private _defaultSplitTask = tasks.defaultSplit(this, {
    // Without a history head, the commit's own history decides; a commit outside of it gets every reported environment.
    preset: () => this.report?.flakinessProject ? { type: 'commit', commitId: this.report.commitId, historyHead: this.historyHead } : undefined,
    project: () => this._project,
    snapshotId: () => this._snapshotId,
  });

  // A report outside of a project has no history, so "All Environments" is every reported environment.
  // Tasks compare splits by identity, so it is the same object every time.
  private _everyEnvironment = EnvGroup.open();

  private _defaultSplit() {
    return this.report?.flakinessProject ? this._defaultSplitTask.value : this._everyEnvironment;
  }

  private _split() {
    return this.state?.timelineSplit ?? this._defaultSplit();
  }

  // "All Environments" stays out of the state, so that it follows the default split of the history.
  // Once the default is known, a split equal to it is dropped, without a new history entry.
  private _canonicalLinkTask = new Task(this, {
    args: () => [this.state?.timelineSplit, this._defaultSplit()] as const,
    task: async ([split, defaultSplit]) => {
      if (split && defaultSplit && split.isEqual(defaultSplit))
        handleLink(this._router, this._linkify({ timelineSplit: undefined }), { replace: true });
    },
  });

  @query('feature-commit-history-popup') private _featureCommitHistoryPopup?: FeatureCommitHistoryPopup;

  @state() private _showWaterfall = (() => {
    try {
      return localStorage.getItem('fk-waterfall-visible') === 'true';
    } catch {
      return false;
    }
  })();

  private _linkify(update: Partial<FeatureReportViewerState>): AppLink {
    if (!this.state)
      return;
    const newState = { ...this.state, ...update };
    return this.linkifier?.(newState) ?? (() => {
      this.state = newState
    });
  }

  private _filterCache = new SyncComputationCache<{ testIndex: TestIndex, fql: Query }, FilterContext> ({
    size: 10,
    etag: ({ testIndex, fql }) => TestIndex.etagFilterContext(testIndex, fql),
    compute: ({ testIndex, fql }, etag) => {
      return testIndex.createFilterContext(fql, etag);
    }
  });

  private _fqlTask = new ContextTask(this, contexts.fql, {
    args: () => [this.state?.query] as const,
    task: async ([query], { signal }) => {
      return Query.parse(query ?? '');
    }
  });

  private _fqlLinker = new ContextTask(this, contexts.linkFQL, {
    args: () => [this.linkifier],
    task: async ([], { signal }) => {
      return query => this._linkify({ query });
    }
  });

  private _timelineLinker = new ContextTask(this, contexts.linkTimeline, {
    args: () => [this.linkifier, this.report] as const,
    task: async ([linkifier, report], { signal }) => {
      if (!report || report.environments.length <= 1)
        return undefined;
      return timeline => this._linkify({ timelineSplit: timeline });
    }
  });

  private _historyTask = new Task(this, {
    args: () => [
      this.historyHead,
      this.commit, this._project, this._snapshotId,
      this._wireReportTask.value,
      this._testIndexAndCutoffTask.value,
      this._timelinesTask.value,
      this._commitRegistry,
      this.state?.historyType,
    ] as const,
    task: async ([historyHead, commit, project, snapshotId, wireReport, testIndexAndCutoff, timelines, commitRegistry, historyType], { signal }) => {
      CHECK(commit && project && snapshotId && testIndexAndCutoff && timelines && wireReport && historyHead && commitRegistry);

      const testsMask = Ranges.capAt(wireReport.testsMask(testIndexAndCutoff.testIndex), testIndexAndCutoff.cutoffIndex);
      const [commitOutcomesResponse, testsHistoryResponse] = await Promise.all([
        api.report.commitOutcomesExpanded.POST({
          orgSlug: project.org.orgSlug,
          projectSlug: project.projectSlug,
          snapshotId,
          timeZoneId: project.timezone,
          cutoffTestIndex: testIndexAndCutoff.cutoffIndex,
          commitId: commit.commitId,
          timelines: timelines.map(t => t.serialize()),
        }, { signal }),
        api.report.testsHistory.POST({
          orgSlug: project.org.orgSlug,
          projectSlug: project.projectSlug,
          snapshotId,
          historyHead,
          // URL override wins; otherwise follow the project's default history type.
          historyType: historyType ?? project.defaultHistoryType,
          timeZoneId: project.timezone,
          tests: testsMask,
          timelines: timelines.map(t => t.serialize()),
        }, { signal })
      ]);

      const timelineToCommitOutcomes = new Map<string, T.TestOutcomes>();
      const timelineToTestHistory = new Map<string, TestHistory[]>();
      for (const { timeline, testOutcomes } of commitOutcomesResponse)
        timelineToCommitOutcomes.set(EnvGroup.deserialize(timeline).etag(), testOutcomes);
      for (const { timeline, history } of testsHistoryResponse.timelines) {
        timelineToTestHistory.set(EnvGroup.deserialize(timeline).etag(), history.map(h => ({
          historyType: h.historyType,
          sinceTimestamp: h.sinceTimestamp,
          commitId: h.commitId,
          testOutcomes: h.testOutcomes,
          durations: h.durations.map(h => Histogram.decompress(h)),
        })));
      }
      // Add history commits and the main commit.
      commitRegistry.addCommits(testsHistoryResponse.commits);
      return {
        timelines,
        timelineToCommitOutcomes,
        timelineToTestHistory,
      };
    }
  });

  private _wireReportTask = new Task(this, {
    args: () => [
      this.report,
    ] as const,
    task: async ([report], { signal }) => {
      CHECK(report);
      const result = Schema.Report.safeParse(report);
      if (!result.success)
        throw result.error;
      return new WireReport(ReportUtils.normalizeReport(result.data as any));
    }
  });

  private _testIndexAndCutoffTask = new Task(this, {
    args: () => [this._project, this._snapshotId, this._wireReportTask.value] as const,
    task: async ([project, snapshotId, wireReport], { signal }) => {
      CHECK(wireReport);
      const jsonTestIndex = project && snapshotId ? await api.report.testIndex.GET({
        orgSlug: project.org.orgSlug, projectSlug: project.projectSlug, snapshotId,
      }, { signal }) : Stats.createEmptyTests();
      const builder = new TestIndexBuilder(jsonTestIndex);
      const cutoffIndex = builder.cutoffIndex();
      builder.addReport(wireReport.report);
      return { cutoffIndex, testIndex: builder.build() };
    },
  });

  private _timelinesTask = new Task(this, {
    args: () => [
      this._wireReportTask.value, this._split(), this.state?.testTimeline
    ] as const,
    task: async ([wireReport, timelinesSplit, testTimeline], { signal }) => {
      CHECK(wireReport && timelinesSplit);
      const timelines = testTimeline ? [testTimeline] : timelinesSplit.subgroups(wireReport.envs);
      return timelines;
    }
  });

  private _commitAnalyzerTask = new Task(this, {
    args: () => [this.reportId, this.commit, this.report, this._testIndexAndCutoffTask.value] as const,
    task: async ([reportId, commit, report, testIndexAndCutoff], { signal }) => {
      CHECK(report && commit && testIndexAndCutoff);
      this._commitRegistry?.addCommits([commit]);
      const builder = StatsBuilder.create(testIndexAndCutoff.testIndex, undefined);
      builder.addRun(reportId ?? 0 as Stats.RunId, report, {
        durationCompressionMaxRelativeError: 0,
        durationCompressionMinAbsoluteErrorMs: 0,
      });
      const stats = new StatsAnalyzer(builder.jsonStats());
      return stats.getCommitAnalyzer(commit.commitId);
    }
  });

  private _commitStatsTask = new Task(this, {
    args: () => [this._project, this._snapshotId, this.commit, this._split()] as const,
    task: async ([project, snapshotId, commit, timelineSplit], { signal }) => {
      if (!project || !snapshotId || !commit || !timelineSplit)
        return undefined;
      try {
        const response = await api.history.commitStats.POST({
          orgSlug: project.org.orgSlug,
          projectSlug: project.projectSlug,
          snapshotId,
          commitOptions: {
            head: commit.commitId,
            maxCount: 1,
          },
          pageOptions: {
            number: 0, size: 1,
          },
          timelineSplit: timelineSplit.serialize(),
        }, { signal });
        return response.elements[0];
      } catch {
        return undefined;
      }
    }
  });

  private _testStatsTask = new Task(this, {
    args: () => [ this._testsReportTask.value, this.state?.testId, this.state?.testTimeline ] as const,
    task: async ([report, testId, testTimeline ], { signal }) => {
      CHECK(report && testId && testTimeline);
      return report.findTestStats(testId, testTimeline);
    }
  });

  private _testLinkRenderer = new ContextTask(this, contexts.linkTest, {
    args: () => [this._project] as const,
    task: async ([project], { signal }) => {
      return (testStats, revealId) => this._linkify({
        timelineSplit: this.state?.timelineSplit,
        plane: this.state?.plane,
        query: this.state?.query,
        testTimeline: EnvGroup.deserialize(testStats.timeline),
        testId: testStats.test.testId,
        testAttempt: testStats.attemptIdx,
      }) + (revealId ? `#${revealId}` : '');
    }
  });

  private _unfilteredTestsReportTask = new Task(this, {
    args: () => [
      this._commitAnalyzerTask.value, this._timelinesTask.value, this._testIndexAndCutoffTask.value,
      this._historyTask.value,
    ] as const,
    task: async ([commitAnalyzer, timelines, testIndexAndCutoff, history], { signal }) => {
      CHECK(commitAnalyzer && timelines && testIndexAndCutoff);
      const { testIndex } = testIndexAndCutoff;
      const span = new SpanAnalyzer([commitAnalyzer]);
      const timelineTestsReports = history ? history.timelines.map(timeline => {
        // Local reports have not being aggregated to the commit yet.
        // In this case, we take unhealthy tests from the commit outcomes.
        // However, non-local, aggregated report computes unhealthy run tests using
        // the T.unhealthyTestsForAggregatedRun primitive.
        const hCommitOutcomes = history.timelineToCommitOutcomes.get(timeline.etag()) ?? T.newOutcomes();
        const unhealthyRunTests = this.reportId
          ? T.unhealthyTestsForAggregatedRun(hCommitOutcomes)
          : T.unhealthyTests(hCommitOutcomes);
        return TimelineTestsReport.create(ResolvedTimeline.create(testIndex, timeline), span, 'eod').regressify(unhealthyRunTests).setHistory(history.timelineToTestHistory.get(timeline.etag()) ?? []);
      }) : timelines.map(timeline => {
        // If there is no history, then we cannot detect regressions, and then
        // failures are shown as failures. In other words, all tests are considered to be "unhealthy".
        return TimelineTestsReport.create(ResolvedTimeline.create(testIndex, timeline), span, 'eod').regressify(Ranges.complement(T.EMPTY_TESTS));
      })
      return new TestsReport(testIndexAndCutoff.testIndex, timelineTestsReports, this._project?.acceptableFlakinessRatio ?? 0);
    }
  });

  private _testsReportTask = new Task(this, {
    args: () => [
      this._unfilteredTestsReportTask.value, this._fqlTask.value,
    ] as const,
    task: async ([unfilteredTestsReport, fql], { signal }) => {
      CHECK(unfilteredTestsReport && fql);
      const filter = this._filterCache.get({ testIndex: unfilteredTestsReport.testIndex(), fql });
      return unfilteredTestsReport.applyFilter(filter);
    }
  });

  private _countersTask = new Task(this, {
    args: () => [
      this._unfilteredTestsReportTask.value, this._fqlTask.value,
    ] as const,
    task: async ([unfilteredTestsReport, fql], { signal }) => {
      CHECK(unfilteredTestsReport && fql);
      fql = fql.clearStatusFilters();
      const filter = this._filterCache.get({ testIndex: unfilteredTestsReport.testIndex(), fql });
      return unfilteredTestsReport.applyFilter(filter).totalOutcomes();
    }
  });

  private _testRunAttemptsTask = new Task(this, {
    args: () => [
      this._wireReportTask.value, this._testStatsTask.value,
    ] as const,
    task: async ([wireReport, testStats], { signal }) => {
      CHECK(wireReport && testStats);

      const test = wireReport.fkTest(testStats.test.testId);
      CHECK(test);
      const attempts = test.attempts.filter(attempt => wireReport.envs[attempt.environmentIdx ?? 0].envId === testStats.env.envId);
      CHECK(attempts.length);
      return attempts;
    },
  });

  private _dataTableProviderTask = new Task(this, {
    args: () => [
      this._project, this._wireReportTask.value, this._testsReportTask.value, this.state?.plane, this._historyTask.value, this.commit, this._commitRegistry
    ] as const,
    task: async ([project, wireReport, testsReport, plane, history, commit, commitRegistry], { signal }) => {
      CHECK(wireReport && testsReport && commitRegistry);
      const timezone = project?.timezone ?? Temporal.Now.timeZoneId();
      plane ??= 'tests';
      if (plane === 'tests') {
        const statusColumn = createStatusColumn({
          commitRegistry,
          runLinker: (testStats) => this._linkify({
            testTimeline: EnvGroup.deserialize(testStats.timeline),
            testId: testStats.test.testId,
            testAttempt: testStats.attemptIdx,
            timelineSplit: EnvGroup.deserialize(testStats.timeline),
          }),
        });
        const historyColumn = history && commit ? createTestHistoryColumn<WireTypes.TestStats>({
          history: testStats => testStats.history,
          commitRegistry,
          setting: { settingId: 'test-stats.history', title: 'History' },
          name: html`<report-history-selector
            .historyType=${this.state?.historyType}
            @fk-history-change=${(e: FKReportHistorySelectEvent) => handleLink(this._router, this._linkify({ historyType: e.historyType, page: 0 }))}
          ></report-history-selector>`,
          timezone,
          onHistorySelected: (testStats, testHistory) => this._onHistorySelected(testStats, testHistory),
        }) : undefined;
        return {
          columns: [
            historyColumn,
            statusColumn,
            testStatsColumns.name,
            testStatsColumns.timeline,
            testStatsColumns.duration,
            historyColumn ? createDurationTrendColumn<WireTypes.TestStats>() : undefined,
            historyColumn ? testStatsColumns.flipRate : undefined,
            historyColumn ? testStatsColumns.failRate : undefined,
          ].filter(c => !!c),
          loader: createTestStatsLoaderLocal(testsReport),
          defaultSortColumn: statusColumn,
        } satisfies DataTableProvider<WireTypes.TestStats, WireTypes.TestStatsSortAxis>;
      }
      if (plane === 'timelines') {
        return createTimelineStatsDataProvider({ 
          project: this._project,
          loader: createTimelineStatsLoaderLocal(testsReport),
        });
      }
      if (plane === 'errors')
        return createErrorStatsDataProvider({ loader: createErrorStatsLoaderLocal(testsReport) });
      if (plane === 'tags')
        return createTagStatsDataProvider({ loader: createTagStatsLoaderLocal(testsReport) });
      if (plane === 'annotations')
        return createAnnotationStatsDataProvider({ loader: createAnnotationStatsLoaderLocal(testsReport) });
    }
  });
  
  private _renderUnattributedErrors() {
    const errors = this.report?.unattributedErrors;
    if (!errors?.length)
      return nothing;
    return errors.map(error => {
      const text = error.stack ? error.stack : error.message ?? error.value ?? '';
      return html`
        <fk-callout variant=danger>
          <ansi-text .text=${text}></ansi-text>
        </fk-callout>
      `;
    });
  }

  private _renderErrors(err: ZodError, report: unknown) {
    const shown = err.issues.slice(0, 10);
    const remaining = err.issues.length - shown.length;
    return html`
      <v-box>
        ${this._renderHeader('is-error')}

        <sl-divider></sl-divider>
        <fk-callout variant=danger>
          <v-box style="gap: var(--sl-spacing-small);">
            <span style="font-weight: var(--sl-font-weight-semibold); color: var(--sl-color-danger-700);">Report schema validation failed</span>
            ${shown.map(issue => html`
              <h-box style="gap: var(--sl-spacing-small); align-items: baseline;">
                <code style="color: var(--sl-color-neutral-500); white-space: nowrap;">${issue.path.join('.')}</code>
                <span>${issue.message}</span>
              </h-box>
            `)}
            ${remaining > 0 ? html`<span style="color: var(--sl-color-neutral-500);">... ${remaining} more</span>` : nothing}
          </v-box>
        </fk-callout>
        <pre style="white-space: pre-wrap;">${JSON.stringify(report, null, 2)}</pre>
      </v-box>
    `;
  }

  private _renderHeader(isErrorReport?: 'is-error') {
    const homeLink = this._linkify({
      page: undefined,
      pageSize: undefined,
      plane: undefined,
      query: undefined,
      sortDirection: undefined,
      sortName: undefined,
      testAttempt: undefined,
      testId: undefined,
      testTimeline: undefined,
    });
    const title = this.report?.title ? html` <sl-icon name=dot style=" color: var(--sl-color-neutral-400);"></sl-icon> ${this.report.title}` : '';

    let osNames: string[] = [];
    let commonKeys: [string, string][] = [];
    let runnerTags: string[] = [];
    let reporterTag: string|undefined;
    if (!isErrorReport) {
      osNames = !isErrorReport ? Array.from(new Set((this.report?.environments ?? []).map(env =>
        EnvGroup.osLabel(env.systemData ?? {})
      ))).filter(name => name.trim().length > 0) : [];

      const metadatas = this.report?.environments?.map(env => env.metadata ?? env.userSuppliedData ?? {}) ?? [];
      commonKeys = Object.entries(metadatas[0] ?? {}).filter(([key, value]) => typeof value !== 'object' && metadatas.every(meta => meta[key] === value)).map(([key, value]) => [key, String(value)]);

      const { testRunner, runtime, generatedBy } = this.report ?? {};
      if (testRunner)
        runnerTags.push(`${testRunner.name} ${testRunner.version}`);
      if (runtime)
        runnerTags.push(`${runtime.name} ${runtime.version}`);
      if (generatedBy)
        reporterTag = `${generatedBy.name} ${generatedBy.version}`;
    }

    return html`
      <v-box style="gap: var(--sl-spacing-small);">
        ${this.commit ? html`
          <div class=second-column>
            <fk-commitstats noduration style="background: var(--sl-color-neutral-50);"
              .stats=${this._commitStatsTask.value}
              .commit=${this.commit}
            ></fk-commitstats>
          </div>
          <h-box>
            <button class=${classMap({
              'waterfall-toggle': true,
              'toggled': this._showWaterfall,
            })} @click=${() => {
              this._showWaterfall = !this._showWaterfall;
              try { localStorage.setItem('fk-waterfall-visible', String(this._showWaterfall)); } catch {}
            }}><sl-icon name=chevron-right></sl-icon>Tests Waterfall</button>
            <sl-divider style="flex: auto;"></sl-divider>
          </h-box>
        ` : nothing}
        ${this._showWaterfall ? html`<test-waterfall
          .report=${this._wireReportTask.value?.report}
          .testRun=${(this._testRunAttemptsTask.value ?? []).at(this.state?.testAttempt ?? 0)}
          @fk-testrun-selected=${(event: CustomEvent<{ report: FlakinessReport.Report, test: FlakinessReport.Test, attempt: FlakinessReport.RunAttempt, }|undefined>) => {
            if (!this.state)
              return;
            if (!event.detail) {
              handleLink(this._router, this._linkify({
                testId: undefined,
                testTimeline: undefined,
                testAttempt: 0,
              }));
            } else {
              const { report, test, attempt } = event.detail;
              const wireReport = new WireReport(report);
              const timeline = (this._split() ?? EnvGroup.open()).subgroups([wireReport.envs[event.detail.attempt.environmentIdx ?? 0]])[0];
              const allAttempts = test.attempts.filter(a => timeline.accepts(wireReport.envs[a.environmentIdx ?? 0]));
              handleLink(this._router, this._linkify({
                testId: wireReport.wireTest(test)!.testId,
                testTimeline: timeline,
                testAttempt: allAttempts.indexOf(attempt),
              }));
            }
          }}
        ></test-waterfall><sl-divider></sl-divider>
        ` : nothing}
        <h-box style="gap: var(--sl-spacing-small);">
          ${this.report?.category ? renderCategoryIconColor(this.report?.category, { klass: 'title-icon' }) : nothing}
          <a class=title ${hrefOrClick(homeLink)}>
            <h-box style="gap: 0;">
              ${this.reportId ? `Run #${this.reportId}` : `Tests Report`}
              ${title}
            </h-box>
          </a>
          <slot name=title></slot>
        </h-box>

        <div class=details>
          <h-box style="gap: var(--sl-spacing-2x-small);">
            <sl-icon name=calendar></sl-icon>
            <sl-format-date month="long" day="numeric" year="numeric" hour="numeric" minute="numeric" date=${new Date(this.report?.startTimestamp ?? Date.now()).toISOString()}></sl-format-date>
          </h-box>
          <sl-icon name=dot></sl-icon>
          <time-interval icon .ms=${this.report?.duration}></time-interval>
          ${this.report?.url ? html`
            <sl-icon name=dot></sl-icon>
            <a-ext href=${this.report.url}>Run Logs</a-ext>
          ` : nothing}
        </div>
        ${osNames.length || commonKeys.length || runnerTags.length || reporterTag ? html`
          <h-box>
            ${osNames.map(osName => html`<sl-tag variant=primary pill>${osName}</sl-tag>`)}
            ${runnerTags.map(tag => html`<sl-tag variant=primary pill>${tag}</sl-tag>`)}
            ${commonKeys.map(([key, value]) => html`<sl-tag variant=primary pill>${key}: ${value}</sl-tag>`)}
            ${reporterTag ? html`<sl-tag variant=primary pill>${reporterTag}</sl-tag>` : nothing}
          </h-box>
        ` : nothing}
      </v-box>
    `
  }

  override render() {
    if (this.report && this._wireReportTask.error instanceof ZodError)
      return this._renderErrors(this._wireReportTask.error, this.report);

    const wireReport = this._wireReportTask.value;
    if (!wireReport)
      return nothing;

    const hasHistory = !!this._historyTask.value;

    return html`
      <feature-commit-history-popup></feature-commit-history-popup>
      <v-box style="gap: var(--sl-spacing-large);"
        @fk-history-requested=${(event: FKHistoryRequested) => {
          this._onHistorySelected(event.testStats);
        }}
      >
        ${this._renderHeader()}
        ${this._renderUnattributedErrors()}

        ${(this.report?.environments.length ?? 0) > 1 ? html`
          <timeline-selector multiple
            .envs=${wireReport.envs}
            .split=${this._split()}
            .defaultSplit=${this._defaultSplit()}
            @fk-select=${(event: CustomEvent<EnvGroup>) => {
              handleLink(this._router, this._linkify({ timelineSplit: event.detail }));
            }}
          ></timeline-selector>
        ` : nothing}

        ${this.state?.testId ? this._renderTest({ testStats: this._testStatsTask.value, wireReport }) : html`
          <h-box style="align-items: flex-start;">
            <fql-search q=${this.state?.query ?? ''} @fql-changed=${(e: FqlChangedEvent) => {
              handleLink(this._router, this._linkify({ query: e.query, page: 0 }));
            }}></fql-search>
          </h-box>

          <report-outcomes ?hide-regressions=${!hasHistory} .outcomes=${this._countersTask.value}></report-outcomes>

          <fk-tabbar selected=${this.state?.plane ?? 'tests'}>
            <fk-tab
              name=tests
              data-testid=plane-tests
              .link=${this._linkify({ plane: 'tests', page: 0, sortDirection: undefined, sortName: undefined, })}
            >
              Tests (${this._testsReportTask.value?.counters()?.tests ?? 0})
            </fk-tab>
            ${(this.report?.environments.length ?? 0) > 1 ? html`
              <fk-tab
                name=timelines
                data-testid=plane-timelines
                .link=${this._linkify({ plane: 'timelines', page: 0, sortDirection: undefined, sortName: undefined, })}
              >
                Environments (${this._testsReportTask.value?.counters()?.timelines ?? 0})
              </fk-tab>
            ` : nothing}
            <fk-tab
              name=errors
              data-testid=plane-errors
              .link=${this._linkify({ plane: 'errors', page: 0, sortDirection: undefined, sortName: undefined, })}
            >
              Errors (${this._testsReportTask.value?.counters()?.errors ?? 0})
            </fk-tab>
            <fk-tab
              name=tags
              data-testid=plane-tags
              .link=${this._linkify({ plane: 'tags', page: 0, sortDirection: undefined, sortName: undefined, })}
            >
              Tags (${this._testsReportTask.value?.counters()?.tags ?? 0})
            </fk-tab>
            <fk-tab
              name=annotations
              data-testid=plane-annotations
              .link=${this._linkify({ plane: 'annotations', page: 0, sortDirection: undefined, sortName: undefined, })}
            >
              Annotations (${this._testsReportTask.value?.counters()?.annotations ?? 0})
            </fk-tab>

            <fk-tabpane>
              <data-table
                .pageNumber=${this.state?.page}
                .pageSize=${this.state?.pageSize}
                .sortAxis=${this.state?.sortName}
                .sortDirection=${this.state?.sortDirection}
                .provider=${this._dataTableProviderTask.value}
                .linker=${this._linkify.bind(this) satisfies DataTableLinker}
              >
                <no-data-message
                  .state=${this.state}
                  .linker=${this._linkify.bind(this)}
                ></no-data-message>
              </data-table>
            </fk-tabpane>
          </fk-tabbar>
        `}
      </v-box>
    `;
  }

  private _onHistorySelected(testStats: WireTypes.TestStats, options?: TestHistorySnippetSelection) {
    if (!this.state)
      return;
    const state = {
      timelineSplit: this.state.timelineSplit,
      head: this.historyHead,
      testId: testStats.test.testId,
      testTimeline: EnvGroup.deserialize(testStats.timeline),
    };
    if (options)
      void this._featureCommitHistoryPopup?.showHistoryBar({ testStats, selection: options, state });
    else
      this._featureCommitHistoryPopup?.show({ testStats, state });
  }

  private _renderTest(options: {
    wireReport: WireReport,
    testStats?: WireTypes.TestStats,
  }) {
    const { wireReport, testStats } = options;
    if (!testStats || !this._commitRegistry)
      return nothing;
    const history = this._historyTask.value;
    const tz = this._project?.timezone ?? Temporal.Now.timeZoneId();
    return html`
      <sl-divider style="flex: auto;"></sl-divider>
      <h-box>
        <teststats-title .testStats=${testStats}>
          ${history ? html`
            <sl-icon-button slot=suffix style="font-size: var(--sl-font-size-x-large);" name=activity @click=${() => {
              this._onHistorySelected(testStats);
            }}></sl-icon-button>
          ` :
          nothing}
        </teststats-title>
        <x-filler></x-filler>
        ${history && tz ? createTestHistorySnippet({
          timezone: tz,
          history: testStats.history,
          commitRegistry: this._commitRegistry,
          onHistorySelected: testHistory => this._onHistorySelected(testStats, testHistory)
        }) : nothing}
      </h-box>
      <test-run
        .attachmentURLBuilder=${this.attachmentURLBuilder}
        .wireReport=${wireReport}
        .attempts=${this._testRunAttemptsTask.value}
        .isRegression=${testStats.outcome === 'regressed'}
        .attemptIdx=${this.state?.testAttempt}
        .linkifier=${(options: TestRunState) => this._linkify({ testAttempt: options.attemptIdx })}
      ></test-run>
    `;
  }

  static styles = [linkStyles, pageStyles, css`
    .title {
      font-size: var(--sl-font-size-2x-large);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .title-icon {
      font-size: var(--sl-font-size-2x-large);
    }

    test-run {
      margin-top: var(--sl-spacing-x-large);
    }

    .details {
      font-size: var(--sl-font-size-small);
      color: var(--sl-color-neutral-500);
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
    }

    .waterfall-toggle {
      all: unset;
      appearance: none;
      font-size: var(--sl-font-size-small);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--sl-spacing-2x-small);
      cursor: pointer;

      &.toggled {
        color: var(--sl-color-primary-600);
      }

      &.toggled sl-icon {
        rotate: 90deg;
      }
    }
  `]
}
